import React from 'react'
import { type FormikContextType, useFormikContext } from 'formik'
import { Box, Grid, FormControl, InputLabel, MenuItem, Select, FormHelperText, Typography, Switch } from '@mui/material'
import { type AssetProjectUpdateDto } from '../../../api-schema-types/assets/AssetProjectCrudDtos'
import { FormikFieldErrors } from '../../components/ui-widgets/FormikFieldErrors'
import FormikTextField from '../../components/ui-widgets/FormikTextField'

import { type EditSolarProjectComponentProps } from './EditSolarProjectForm'

const EditDealModelBilling: React.FC<EditSolarProjectComponentProps> = ({
  project,
  overrideLockedFields
}: EditSolarProjectComponentProps) => {
  const formikProps: FormikContextType<AssetProjectUpdateDto> = useFormikContext()
  const formikErrors = new FormikFieldErrors(formikProps)

  const isDealModelLocked = (): boolean => {
    return !overrideLockedFields && project.dealModel.isLocked
  }

  const isBillingLocked = (): boolean => {
    return !overrideLockedFields && project.billing.isLocked
  }

  const handleUseIRRRangeChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (!event.target.checked) {
      formikProps.setFieldValue('dealModel.useIRRRange', false)
    } else if (event.target.checked) {
      formikProps.setFieldValue('dealModel.useIRRRange', true)
    }
  }

  return (
    <>

      {/* ==================================================================================================== */}

      <Box sx={{ pb: 4 }}>
        <h3 className="">Deal model</h3>

        <Grid container spacing={4}>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Project Currency</InputLabel>
              <Select
                {...formikProps.getFieldProps('dealModel.projectCurrencyId')}
                label={'Project Currency'}
                disabled={isDealModelLocked()}
                error={formikErrors.fieldInError('dealModel.projectCurrencyId')}
              >
                {
                  project.lookups.enabledCurrencies.map((c) =>
                    <MenuItem key={c.currencyId} value={c.currencyId}>{c.code}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('dealModel.projectCurrencyId')}</FormHelperText>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.cellCost'} label={'Cell cost'} type={'number'}
                             locked={isDealModelLocked()}
                             adornment={project.dealModel.projectCurrencyCode}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.numberOfCells'} label={'Number of cells'}
                             type={'number'} locked={isDealModelLocked()}/>
          </Grid>

          <Grid item md={3}></Grid>

          {/* ==================================================================================================== */}

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.electricityCost'} label={'Electricity cost'}
                             type={'number'}
                             locked={isDealModelLocked()}
                             adornment={project.billing.billingCurrencyCode}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.leaseTermYears'} label={'Lease term'}
                             type={'number'}
                             locked={isDealModelLocked()} adornment={'years'}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.estimatedCellRental'}
                             label={'Estimated rental per cell'} type={'number'}
                             locked={isDealModelLocked()}
                             adornment={project.billing.billingCurrencyCode}/>
          </Grid>
          <Grid item md={3}>
          </Grid>

          {/* ==================================================================================================== */}

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.estimatedIRR'} label={'Estimated IRR'}
                             type={'number'}
                             locked={isDealModelLocked()} adornment={'%'}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'dealModel.estimatedIRRRange'} label={'Estimated IRR Range'}
                             type={'text'}
                             locked={isDealModelLocked()} />
          </Grid>

          <Grid item md={3}>
            <Typography variant={'body2'}>Use IRR Range</Typography>
            <Switch
              checked={formikProps.values.dealModel.useIRRRange}

              onChange={handleUseIRRRangeChange}
              color="primary"
            />
          </Grid>

          <Grid item md={3}>
          </Grid>

        </Grid>

      </Box>

      {/* ==================================================================================================== */}

      <Box sx={{ pb: 4 }}>
        <h3 className="page-title">Billing</h3>

        <Grid container spacing={4}>
          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Project Billing Type</InputLabel>
              <Select
                {...formikProps.getFieldProps('billing.projectBillingType')}
                label={'Project Billing Type'}
                disabled={isBillingLocked()}
                error={formikErrors.fieldInError('billing.projectBillingType')}
              >
                {
                  project.lookups.projectBillingTypes.map((c) =>
                    <MenuItem key={c.code} value={c.code}>{c.description}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('billing.projectBillingType')}</FormHelperText>
          </Grid>

          <Grid item md={3}>
            <FormControl sx={{ width: '100%' }}>
              <InputLabel>Billing Currency</InputLabel>
              <Select
                {...formikProps.getFieldProps('billing.billingCurrencyId')}
                label={'Project Currency'}
                disabled={isBillingLocked()}
                error={formikErrors.fieldInError('dealModel.billingCurrencyId')}
              >
                {
                  project.lookups.enabledCurrencies.map((c) =>
                    <MenuItem key={c.currencyId} value={c.currencyId}>{c.code}</MenuItem>
                  )
                }
              </Select>
            </FormControl>
            <FormHelperText>{formikErrors.getError('billing.billingCurrencyId')}</FormHelperText>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'billing.managementFee'} label={'Management Fee'}
                             type={'number'}
                             locked={isBillingLocked()} adornment={'% / 100'}/>
          </Grid>

          <Grid item md={3}>
            <FormikTextField formikProps={formikProps} fieldName={'billing.preDistributionDeductions'} label={'Pre Distribution Deductions'}
                             type={'number'}
                             locked={isBillingLocked()} adornment={project.billing.billingCurrencyCode}/>
          </Grid>
        </Grid>

      </Box>

      {/* ==================================================================================================== */}

    </>
  )
}

export default EditDealModelBilling
